import React, { Component } from 'react';

import PropTypes from 'prop-types';

import {
    FadeInOut,
    FadeInOutContainer,
} from 'wanderu-animations';
import A from 'wanderu-component-react-router-link';
import {
    Control,
    SplitTest,
    Variant,
} from 'wanderu-component-split-test';
import { Layout, WDSButton } from 'wanderu-components';
import {
    CHEAPEST,
    EARLIEST,
    FASTEST,
    LATEST,
    RECOMMENDED,
} from 'wanderu-ducks-search';
import { localize } from 'wanderu-i18n';
import { durationToHoursAndMinutes } from 'wanderu-utils';

import arrow from 'wanderu-assets/images/left-black-arrow.svg';
import sliders from 'wanderu-assets/images/sliders.svg';

import { DEPART_DIRECTION } from '../../../constants';
import FilterButton from '../FilterButton/FilterButton';

import styles from './MobileFilters.scss';

class MobileFilters extends Component {
    static propTypes = {
        activeSortType: PropTypes.string.isRequired,
        containerWidth: PropTypes.number,
        departDate: PropTypes.string.isRequired,
        destinationCityName: PropTypes.string.isRequired,
        direction: PropTypes.string.isRequired,
        i18nMoment: PropTypes.func,
        i18nMoney: PropTypes.func,
        numberOfActiveFilters: PropTypes.number.isRequired,
        onMapClick: PropTypes.func,
        onSelectSortType: PropTypes.func.isRequired,
        onToggleFilterMenu: PropTypes.func.isRequired,
        originCityName: PropTypes.string.isRequired,
        returnDate: PropTypes.string,
        showMap: PropTypes.bool,
        sortOptions: PropTypes.object.isRequired,
        t: PropTypes.func,
        url: PropTypes.string.isRequired,
    };

    constructor(props) {
        super(props);

        this.state = {
            activeSortType: props.activeSortType,
            headerWidth: undefined,
            textOverflow: false,
        };

        this.searchFilters = React.createRef();
        this.headerRef = React.createRef();
    }

    componentDidMount() {
        if (
            this.props.originCityName
            && this.props.destinationCityName
        ) {
            this.setHeaderWidth();
        }
    }

    componentDidUpdate(prevProps, prevState) {
        const {
            activeSortType,
            containerWidth,
            destinationCityName,
            originCityName,
        } = this.props;

        const {
            headerWidth,
        } = this.state;

        if (prevProps.activeSortType !== activeSortType) {
            this.setScrollLeft(activeSortType);
        }

        if (
            prevProps.originCityName !== originCityName
            || prevProps.destinationCityName !== destinationCityName
        ) {
            this.setHeaderWidth();
        }

        if (
            !prevState.headerWidth
            && headerWidth
        ) {
            const textOverflow = headerWidth >= containerWidth;

            this.setState(() => ({
                textOverflow,
            }));
        }

        if (
            headerWidth
            && prevProps.containerWidth !== containerWidth
        ) {
            const textOverflow = headerWidth >= containerWidth;

            this.setState(() => ({
                textOverflow,
            }));
        }
    }

    setScrollLeft = (activeSortType) => {
        let scrollLeft = 0;

        if (
            activeSortType !== RECOMMENDED
            && activeSortType !== CHEAPEST
        ) {
            scrollLeft = this.searchFilters.current.scrollWidth;
        }

        this.searchFilters.current.scrollLeft = scrollLeft;
    };

    setHeaderWidth = () => {
        if (this.headerRef.current) {
            const headerRect = this.headerRef.current.getBoundingClientRect();

            const headerLeftMargin = 19 + 38;

            this.setState(() => ({
                headerWidth: headerRect.width + headerLeftMargin,
            }));
        }
    };

    renderSortOptionData(type, data) {
        const {
            i18nMoment,
            i18nMoney,
            t,
        } = this.props;

        if (
            data === undefined
            || data === 0
        ) {
            return undefined;
        }

        if (type === RECOMMENDED) {
            return t('total-trips', {
                totalTrips: data,
            });
        }

        if (type === FASTEST) {
            const duration = durationToHoursAndMinutes(
                i18nMoment.duration(data),
            );

            if (duration.days > 0) {
                return t('baseApp:trip-duration_days', duration);
            }

            return t('baseApp:trip-duration_hours', duration);
        }

        if (type === CHEAPEST) {
            const price = i18nMoney(data).getTotalPrice();

            return i18nMoney.format(price);
        }

        if (type === EARLIEST || type === LATEST) {
            return i18nMoment.parseZone(data).format('LT');
        }

        return undefined;
    }

    render() {
        const {
            activeSortType,
            departDate,
            destinationCityName,
            direction,
            i18nMoment,
            numberOfActiveFilters,
            onMapClick,
            onSelectSortType,
            onToggleFilterMenu,
            originCityName,
            returnDate = '',
            showMap = false,
            sortOptions,
            t,
            url,
        } = this.props;
        const {
            textOverflow,
        } = this.state;

        const returnDateTimestamp = i18nMoment(returnDate).shortDateTimestamp();
        const departDateTimestamp = i18nMoment(departDate).shortDateTimestamp();
        const isDepartDirection = direction === DEPART_DIRECTION;

        const departText = textOverflow
            ? t('to-destination', {
                destinationName: destinationCityName,
            })
            : t('origin-to-destination', {
                destinationName: destinationCityName,
                originName: originCityName,
            });

        const returnText = textOverflow
            ? t('to-destination', {
                destinationName: originCityName,
            })
            : t('origin-to-destination', {
                destinationName: originCityName,
                originName: destinationCityName,
            });

        return (
            <>
                <Layout className={styles['mobile-container']} data-id="search-header">
                    <A
                        className={styles.arrow}
                        href={url}
                    >
                        <img alt="back" src={arrow} />
                    </A>

                    <FadeInOutContainer
                        animateHeight={false}
                        className={styles['middle-column']}
                        duration={500}
                    >
                        <FadeInOut
                            className={styles['middle-column']}
                            show={isDepartDirection}
                        >
                            <h1
                                className={styles['city-to-city']}
                                ref={isDepartDirection && this.headerRef}
                            >
                                {departText}
                            </h1>

                            <span className={styles['depart-date']}>
                                {t('departs-on', { departDate: departDateTimestamp })}
                            </span>
                        </FadeInOut>

                        <FadeInOut
                            className={styles['middle-column']}
                            show={!isDepartDirection}
                        >
                            <h1
                                className={styles['city-to-city']}
                                ref={!isDepartDirection && this.headerRef}
                            >
                                {returnText}
                            </h1>

                            <span className={styles['depart-date']}>
                                {t('returns-on', {
                                    returnDate: returnDateTimestamp,
                                })}
                            </span>
                        </FadeInOut>
                    </FadeInOutContainer>
                    <SplitTest testName="stationMap">
                        <Control/>
                        <Variant name="showMap">
                            <WDSButton className={styles['map-button']} onClick={onMapClick} variant="unstyled">
                                {showMap
                                    ? 'Hide Map'
                                    : 'Show Map'}
                            </WDSButton>
                        </Variant>
                    </SplitTest>
                </Layout>
                <Layout>
                    <div
                        className={styles['search-filters']}
                        ref={this.searchFilters}
                    >
                        <button
                            className={styles['filter-button']}
                            data-id="filterButton"
                            onClick={onToggleFilterMenu}
                        >
                            <span className={styles['sort-type']}>
                                {t('filters')}
                            </span>

                            <img
                                alt=""
                                className={styles.sliders}
                                src={sliders}
                            />

                            <span
                                aria-label={t('filters')}
                                aria-live="polite"
                                className={styles['filter-number-container']}
                            >
                                {numberOfActiveFilters > 0 && (
                                    <span className={styles['filter-number']}>
                                        {numberOfActiveFilters}
                                    </span>
                                )}
                            </span>
                        </button>

                        {Object.entries(sortOptions).map(([type, data]) => (
                            <FilterButton
                                activeSortType={activeSortType}
                                key={type}
                                onSelectSortType={onSelectSortType}
                                sortOptionData={this.renderSortOptionData(type, data)}
                                type={type}
                            />
                        ))}
                    </div>
                </Layout>
            </>
        );
    }
}

export { MobileFilters };
export default localize(MobileFilters, 'baseApp');
